import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookMarked, Check, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

// School-mission completion. Personal Vocab is written ONLY when the learner taps Save.
export default function MissionComplete({ assignmentId, classId, title, savable: initial }) {
  const { t } = useI18n();
  const [sv, setSv] = useState(initial);
  const [state, setState] = useState("idle"); // idle | saving | saved | dismissed | error
  const [msg, setMsg] = useState("");
  const items = sv?.items || [];
  const unsaved = items.filter((i) => !i.saved);
  const save = async () => {
    setState("saving");
    try { const r = await api.post(`/assignments/${assignmentId}/save-vocabulary`, {}); setSv(r.data); setState("saved"); }
    catch { setMsg(t("missionRun.saveFailed")); setState("error"); }
  };
  const offer = items.length > 0 && sv?.can_save && state !== "dismissed";
  const blocked = items.length > 0 && sv && !sv.can_save;
  return (
    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="nb-card bg-mint p-8 text-center" data-testid="mission-complete">
      <Trophy className="w-12 h-12 mx-auto" aria-hidden />
      <h1 className="font-cabinet font-extrabold text-3xl mt-2">{t("missionRun.complete")}</h1>
      <p className="text-gray-700 mt-1" data-testid="mission-complete-desc">{t("missionRun.completeDesc", { title: "\u0000" }).split("\u0000").map((part, i) => <span key={i}>{i > 0 && <bdi>{title}</bdi>}{part}</span>)}</p>
      {blocked && <p className="text-xs text-gray-600 mt-4" data-testid="mission-vocab-unavailable">{t("missionRun.cantSave")}</p>}
      {offer && (
        <div className="nb-card bg-white p-4 mt-5 text-start" data-testid="mission-vocab-offer">
          {state === "saved" || unsaved.length === 0 ? (
            <p className="font-bold flex items-center gap-2" data-testid="mission-vocab-saved"><Check className="w-4 h-4" /> {t("missionRun.savedToBank")}</p>
          ) : (
            <>
              <p className="font-bold">{t("missionRun.keepWords")}</p>
              <ul className="flex flex-wrap gap-1.5 mt-2" data-testid="mission-vocab-list">
                {items.map((i) => <li key={i.term} className="nb-tag bg-white text-xs normal-case tracking-normal"><bdi>{i.term}</bdi>{i.translation && <span className="text-gray-500"> · <bdi>{i.translation}</bdi></span>}{i.saved && <Check className="w-3 h-3 inline ms-1" />}</li>)}
              </ul>
              {state === "error" && <p className="text-xs text-coral mt-2" role="alert" data-testid="mission-vocab-error">{msg}</p>}
              <div className="flex gap-2 mt-3 flex-wrap">
                <button onClick={save} disabled={state === "saving"} className="nb-btn-primary py-2 px-4 text-sm" data-testid="mission-vocab-save"><BookMarked className="w-4 h-4" /> {t("missionRun.saveToBank")}</button>
                <button onClick={() => setState("dismissed")} className="nb-btn-outline py-2 px-4 text-sm" data-testid="mission-vocab-dismiss">{t("missionRun.notNow")}</button>
              </div>
            </>
          )}
        </div>
      )}
      <div className="flex gap-2 justify-center mt-6 flex-wrap">
        {classId
          ? <Link to={`/my-classes/${classId}`} className="nb-btn-primary px-5 py-3" data-testid="mission-back-to-class">{t("missionRun.backToClass")}</Link>
          : <Link to="/assignments" className="nb-btn-primary px-5 py-3" data-testid="mission-my-assignments">{t("missionRun.myAssignments")}</Link>}
        <Link to="/assignments" className="nb-btn-outline px-5 py-3" data-testid="mission-assignments-link">{t("missionRun.myAssignments")}</Link>
        <Link to="/dashboard" className="nb-btn-outline px-5 py-3" data-testid="mission-dashboard-link">{t("missionRun.dashboard")}</Link>
      </div>
    </motion.div>
  );
}
